import { Head, Link, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { NotificationRow } from "@/components/notifications/notification-row"
import { NotificationsEmptyState } from "@/components/notifications/notifications-empty-state"
import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useIsMobile } from "@/hooks/use-mobile"
import AppLayout from "@/layouts/app-layout"
import { markAllReadNotificationsPath } from "@/routes"
import type {
  NotificationRow as NotificationRowType,
  NotificationsIndexProps,
} from "@/types/notifications"

/**
 * OKS-36 — Full notifications history.
 *
 * Single-column list, paginated 20/page (BE-controlled). Reads same
 * `NotificationRow` component as the panel, just with `variant="page"`
 * for more vertical breathing room and no body truncation per UX Spec
 * §2.
 *
 * Pagination strategy is viewport-dependent (UX Spec §5):
 *   - Desktop (≥768px): BE-supplied prev/next links, full-page visits.
 *   - Mobile (<768px):  IntersectionObserver sentinel + Inertia partial
 *                       reload (`only: ['notifications', 'pagination']`).
 *                       Pages accumulate in component state since
 *                       inertia_rails 3.21 doesn't merge array props
 *                       natively — the partial reload REPLACES, so we
 *                       overlay the accumulator client-side.
 *
 * Mark-all-read is optimistic (UX Spec §4 — badge clears immediately,
 * rows fade unread→read, failure reverts + destructive toast).
 */
export default function NotificationsIndex({
  notifications,
  pagination,
}: NotificationsIndexProps) {
  const { t } = useTranslation()
  const { unreadNotificationsCount } = useCurrentContext()
  const isMobile = useIsMobile()

  // Mobile accumulator: starts with whatever page the controller served
  // on initial mount (typically page 1, but tolerate landing on ?page=N
  // by treating the mounted page as the floor). Subsequent partial
  // reloads bring fresh `notifications` + `pagination.page`; we append
  // when we see a page we haven't accumulated yet. Updating state
  // during render (vs. in a useEffect) follows the React-recommended
  // "store info from previous renders" pattern — keeps the appended
  // list ready in the SAME render as the new `notifications` prop, no
  // cascading effect re-render.
  const [accumulated, setAccumulated] =
    useState<NotificationRowType[]>(notifications)
  const [maxLoadedPage, setMaxLoadedPage] = useState<number>(pagination.page)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  if (pagination.page > maxLoadedPage) {
    setAccumulated((prev) => [...prev, ...notifications])
    setMaxLoadedPage(pagination.page)
  }

  // Optimistic mark-all-read: overlay each visible unread row's read_at
  // and clear the page-level button's hasUnread immediately on click.
  // The Inertia round-trip's partial reload of
  // `unread_notifications_count` brings canonical 0 home for the bell
  // badge (BellTrigger reads from `useCurrentContext()`).
  const [optimisticAllRead, setOptimisticAllRead] = useState(false)
  const [optimisticReadIds, setOptimisticReadIds] = useState<Set<number>>(
    () => new Set(),
  )

  // Mobile infinite-scroll sentinel: load next page when it enters the
  // viewport. Guards against double-fire via `isLoadingMore`. Skipped
  // when there is no `next` (single page or last page).
  useEffect(() => {
    if (!isMobile) return
    if (!pagination.next) return
    if (isLoadingMore) return
    if (typeof IntersectionObserver === "undefined") return
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsLoadingMore(true)
        router.reload({
          only: ["notifications", "pagination"],
          data: { page: pagination.page + 1 },
          preserveUrl: true,
          onFinish: () => setIsLoadingMore(false),
        })
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [isMobile, pagination.next, pagination.page, isLoadingMore])

  function handleMarkAllRead() {
    const visibleUnreadIds = (isMobile ? accumulated : notifications)
      .filter((n) => !n.read_at)
      .map((n) => n.id)

    setOptimisticAllRead(true)
    setOptimisticReadIds(new Set(visibleUnreadIds))

    router.post(
      markAllReadNotificationsPath(),
      {},
      {
        preserveScroll: true,
        onError: () => {
          setOptimisticAllRead(false)
          setOptimisticReadIds(new Set())
          toast.error(t("notifications.error.mark_all_read"))
        },
        onSuccess: () => {
          // Round-trip reconciled: fresh `notifications` arrive with
          // canonical `read_at`. Drop overlays so future renders
          // don't double-account.
          setOptimisticAllRead(false)
          setOptimisticReadIds(new Set())
        },
      },
    )
  }

  const visibleList = isMobile ? accumulated : notifications
  const overlaidList: NotificationRowType[] = visibleList.map((n) =>
    optimisticReadIds.has(n.id) && !n.read_at
      ? { ...n, read_at: new Date().toISOString() }
      : n,
  )
  const hasUnread = !optimisticAllRead && unreadNotificationsCount > 0
  const isAtEnd = overlaidList.length > 0 && pagination.next === null

  return (
    <AppLayout>
      <Head title={t("notifications.page.title")} />
      <main
        data-testid="notifications-page"
        className="mx-auto flex w-full max-w-2xl flex-col px-0 py-6 sm:px-6"
      >
        <header className="flex items-center justify-between gap-3 px-4 pb-4 sm:px-0">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("notifications.page.title")}
          </h1>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasUnread}
            onClick={handleMarkAllRead}
            data-testid="notifications-page-mark-all-read"
          >
            {t("notifications.page.mark_all_read")}
          </Button>
        </header>

        {overlaidList.length === 0 ? (
          <NotificationsEmptyState />
        ) : (
          <ul className="bg-card flex flex-col rounded-md border-y sm:rounded-md sm:border">
            {overlaidList.map((n) => (
              // Key on read state too — when the optimistic overlay
              // flips read_at, NotificationRow's internal `useState`
              // (initialised from prop) needs to remount to pick up
              // the change. Without the suffix, the row would appear
              // unread until a fresh visit replaces the prop tree.
              <li key={`${n.id}-${n.read_at ? "r" : "u"}`}>
                <NotificationRow notification={n} variant="page" />
              </li>
            ))}
          </ul>
        )}

        {isMobile ? (
          pagination.next ? (
            <div
              ref={sentinelRef}
              data-testid="notifications-infinite-scroll-sentinel"
              aria-hidden="true"
              className="h-8 w-full"
            />
          ) : null
        ) : (
          <Pagination pagination={pagination} />
        )}

        {isAtEnd ? (
          <p
            data-testid="notifications-list-end"
            className="text-muted-foreground py-4 text-center text-xs"
          >
            {t("notifications.list.end")}
          </p>
        ) : null}
      </main>
    </AppLayout>
  )
}

/**
 * Prev / page-of / Next pagination footer (desktop only). Mirrors the
 * OKS-17 `CandidatePaginationFooter` pattern — BE-supplied URLs, no
 * FE-side page-number arithmetic. Returns null when there's only one
 * page; the parent renders the end-of-list footer separately.
 */
function Pagination({
  pagination,
}: {
  pagination: NotificationsIndexProps["pagination"]
}) {
  const { t } = useTranslation()
  if (pagination.pages <= 1) return null
  return (
    <nav
      aria-label="Pagination"
      data-testid="notifications-pagination"
      className="flex items-center justify-between gap-2 px-4 py-4 text-sm sm:px-0"
    >
      {pagination.prev ? (
        <Link
          href={pagination.prev}
          className="text-foreground inline-flex items-center gap-1 text-sm"
          data-testid="notifications-pagination-prev"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t("previous", { defaultValue: "Previous" })}
        </Link>
      ) : (
        <span className="text-muted-foreground inline-flex items-center gap-1 text-sm">
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t("previous", { defaultValue: "Previous" })}
        </span>
      )}
      <span className="text-muted-foreground text-xs">
        {pagination.page} / {pagination.pages}
      </span>
      {pagination.next ? (
        <Link
          href={pagination.next}
          className="text-foreground inline-flex items-center gap-1 text-sm"
          data-testid="notifications-pagination-next"
        >
          {t("next", { defaultValue: "Next" })}
          <ChevronRight className="size-4" aria-hidden="true" />
        </Link>
      ) : (
        <span className="text-muted-foreground inline-flex items-center gap-1 text-sm">
          {t("next", { defaultValue: "Next" })}
          <ChevronRight className="size-4" aria-hidden="true" />
        </span>
      )}
    </nav>
  )
}
